import * as React from 'react'
import { moment } from '../utils/moment.service'
import { httpGet, httpGetRoute, httpParamsRemoveDiacriticsToSortableNames, httpPatch, httpPost, httpPut, httpRecoverable } from "../utils/http.service"
import { I18nService } from "../utils/i18n.service"
import { formatDurationFromSecs, formatTime, parseTime, parseTimeRange } from '../utils/utils.service'
import { offeredServiceI18nInterventionPlace } from './offered-services.service'
import { openableDateToWorkingDay } from '../utils/openables.service'
import { CastingBase } from '../json-schemas/base/casting'
import CastingSchema from '../../../../../../json_schemas/base/casting.json'
import { BackofficeCastingBroadastSessionsHistoryResponse } from '../json-schemas/serializers/backoffice-casting-broadast-sessions-history-response'
import { BackofficeCastingBroadcastEmployeesRankingWithMetricsResponse } from '../json-schemas/serializers/backoffice-casting-broadcast-employees-ranking-with-metrics-response'
import { BackofficeCastingsIndexResponse } from '../json-schemas/serializers/backoffice-castings-index-response'
import { BackofficeCastingsCountPerScopeResponse } from '../json-schemas/serializers/backoffice-castings-count-per-scope-response'
import { BackofficeCastingBroadcastToEmployeesResponse } from '../json-schemas/serializers/backoffice-casting-broadcast-to-employees-response'
import { ServiceDeliveryLocationBase } from '../json-schemas/base/service_delivery_location'
import { BackofficeCastingEditPriceAndDiscountResponse } from '../json-schemas/serializers/backoffice-casting-edit-price-and-discount-response'
import { OfferedServiceBase } from '../json-schemas/base/offered_service'
import { EmployeeCastingSetAppointmentProposalResponse } from '../json-schemas/serializers/employee-casting-set-appointment-proposal-response'
import { BackofficeCastingsIndexProps } from '../json-schemas/serializers/backoffice-castings-index-props'
import { CustomerServiceDeliveryPriceBlockDataResponse } from '../json-schemas/serializers/customer-service-delivery-price-block-data-response'
import { ProposalBase } from '../json-schemas/base/proposal'
import { QuotationBase } from '../json-schemas/base/quotation'
import { EmployeeProposalBase } from '../json-schemas/base/employee_proposal'
import { ScheduleBase } from '../json-schemas/base/schedule'
import { LodgingIcalEventBase } from '../json-schemas/base/lodging_ical_event'
import { MarketplaceBase } from '../json-schemas/base/marketplace'
import { SupplierBase } from '../json-schemas/base/supplier'
import TooltipComponent from '../../components/shared/tooltip.component'
import { DayOffBase } from '../json-schemas/base/day_off'
import { toUpperCaseFirst } from '../utils/lodash.service'
import { EmployeeBase } from '../json-schemas/base/employee'
import i18nScopedData from './castings.service.yml'

import type { PaymentMode } from "./payment-transactions.service"
import type { MultiOpeningHours, InterventionPlace } from './offered-services.service'
import type { Props as TooltipComponentProps } from '../../components/shared/tooltip.component'

export interface RansackQuery {
  scope?:
    | 'operator_review_required_not_expired'
    | 'created_no_appointment_not_expired'
    | 'customer_refused'
    | 'waiting_employee_assignment_not_expired'
    | 'created_appointment_not_expired'
    | 'waiting_event_cancelled_assignment'
    | 'waiting_employee_assignment_draft_not_expired'
    | 'waiting_appointment_not_expired'
    | 'waiting_lodging_ical_event_assignment'
    | 'appointment_resolved_in_progress'
    | 'appointment_resolved_proposal_overdue_not_expired'
    | 'quotation_driven_waiting_assignment_not_expired'
    | 'supplier_resolved_not_expired'
    | 'customer_resolved'
    | 'expired_without_proposal'
    | 'expired_proposal_past_date'
    | 'expired_proposal_employee_unavailable'
    | 'schedule_draft_resolved'
    | 'archived_or_supplier_refused'
    | 'not_archived_nor_supplier_refused'
    | 'all'
  'q[s]'?: 'created_at asc' | 'created_at desc'
  'q[id_not_in][]'?: number[]
  'q[id_eq]'?: number
  'q[customer_sortable_name_or_customer_reversed_sortable_name_cont]'?: string
  'q[required_place_eq]'?: InterventionPlace
  'q[city_or_zipcode_cont]'?: string
  'q[service_delivery_location_id_eq]'?: ServiceDeliveryLocationBase['id']
  'q[marketplace_id_eq]'?: MarketplaceBase['id']
  'q[supplier_id_eq]'?: SupplierBase['id']
}

export interface UnserializedSlot {
  duration: number,
  weeks_interval: number,
  start_date: moment.Moment,
  end_date: moment.Moment,
  employees_count?: number
  allowed_patterns: { day_id: number, from_hour: moment.Moment, until_hour: moment.Moment }[]
}

export interface WhyThisEmployeeIsMissingResponse {
  published: { pass: boolean }
  offered_service_id: { pass: boolean }
  covers_casting_location: { pass: boolean, messages: string[] }
  item_preference_ids: { pass: boolean }
  required_employee_ids: { pass: boolean }
  required_place: { pass: boolean }
  market_broadcast_casting: { pass: boolean }
  offered_service_broadcast_casting: { pass: boolean }
  employee_broadcast_casting: { pass: boolean }
  employee_plan: { pass: boolean }
  direct_payment_configured_or_disabled: { pass: boolean }
  casting_custom_price_off_or_directly_paid: { pass: boolean }
  offered_option_ids: { pass: boolean }
  active_urssaf_payment: { pass: boolean }
  customer_types: { pass: boolean }
  presence_of_pets: { pass: boolean }
  has_sap_agreement_if_required: { pass: boolean }
  no_direct_payment_if_eligible_for_coupon: { pass: boolean }
  no_direct_payment_if_eligible_for_customer_plan_subscription: { pass: boolean }
  active_employee_of_customer_invoicing: { pass: boolean }
  cesu_contract_renderable: { pass: boolean, messages: string[] }
  employee_market: Pick<SupplierBase, 'country' | 'employee_extract_transaction_fees_to_distinct_invoice?'>
}

export type CastingMode = CastingBase['casting_mode'];
export type CastingState = CastingBase['state'];
export type CastingArchiveReason = CastingBase['archive_reason'];
export type CastingSlot = CastingBase['slots'][number];
export type CastingRequiredPlace = CastingBase['required_place'];
export type CastingType = CastingBase['casting_type'];
export type Availability = {
  allowed_patterns: {
    inside_pattern: ({
      start_date: string
      duration: number
    } & {
      latitude_before?: number
      longitude_before?: number
      position_at_before?: string
      latitude_after?: number
      longitude_after?: number
      position_at_after?: string
    })[]
    suggested_dates: string[]
  }[]
};
export type CastingDayId = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface SimulateFirstEventsFromUnfinalizedCastingArguments {
  casting: Pick<CastingBase, 'slots'>
  offeredService: Pick<OfferedServiceBase, 'multi_opening_hours'>
  proposal?: {
    duration_empty_or_covered: boolean
    employee_proposals: (Pick<EmployeeProposalBase, 'duration' | 'slot_index' | 'suggested_start_dates'> & { employee: unknown, offered_service_resource: unknown })[]
  }
  limit?: number
  onlyFirstRecurrence?: boolean
}

interface BasicScheduleRule {
  startDate: moment.Moment
  weeksInterval: number
  endDate: moment.Moment
  duration: number
}

export interface CastingPostulateCountinousAssignmentBody {
  schedule_ids: ScheduleBase['id'][]
  lodging_ical_event_selections: { start_date: string, lodging_ical_event_id: LodgingIcalEventBase['id'] }[]
}

const i18nScoped = new I18nService(i18nScopedData)
export const castingArchiveReasons = CastingSchema.properties.archive_reason.enum.filter(_ => _ != null) as CastingArchiveReason[]

export function castingList (query: RansackQuery = {}): Promise<BackofficeCastingsIndexResponse> {
  return httpGet(`/${httpGetRoute()}/castings`, httpParamsRemoveDiacriticsToSortableNames(query))
}

export function castingForAssignableList (assignable: BackofficeCastingsIndexProps['assignable'], query: RansackQuery = {}): Promise<BackofficeCastingsIndexResponse> {
  return httpGet(`/${httpGetRoute()}/${assignable.type.toLocaleLowerCase()}s/${assignable.id}/castings`, httpParamsRemoveDiacriticsToSortableNames(query))
}

export function castingCreate (
  market: Pick<SupplierBase | MarketplaceBase, 'id' | 'market_type'>,
  body: { casting: unknown, service_delivery?: unknown, create_as_quotation_driven?: boolean, send_as_quotation_driven?: boolean }
): Promise<{ proposal_id: ProposalBase['id'], casting: Pick<CastingBase, 'id' | 'state' | 'price_cents'>, quotation_id?: QuotationBase['id'] }> {
  return httpPost(`/${market.market_type === 'Marketplace' ? 'marketplaces' : 'suppliers'}/${market.id}/castings`, body)
}

export function castingUpdate<T extends { casting: { id: number } }> (body: T): Promise<{ casting: Pick<CastingBase, 'id' | 'state'> }> {
  return httpPut(`/${httpGetRoute()}/castings/${ body.casting.id }`, body)
}

export function castingUnassignSupplier (castingId: CastingBase['id']): Promise<{ redirect_to: string }> {
  return httpPatch(`/${httpGetRoute()}/castings/${castingId}/unassign_supplier`, {})
}

export function castingSetUrssafPayment (id: number, urssafPayment: boolean): Promise<void> {
  return httpPost(`/${httpGetRoute()}/castings/${ id }/set_urssaf_payment`, { casting: { urssaf_payment: urssafPayment } })
}

export function castingAddActivity (id: number, body: { activity_type: string, payment_mode: PaymentMode, comment: string }) {
  return httpPatch(`/${httpGetRoute()}/castings/${id}/add_activity`, { casting: body })
}

export function castingArchive (id: number, body: { archive_reason: CastingArchiveReason, other_archive_reason: string }) {
  return httpPatch(`/${httpGetRoute()}/castings/${id}/archive`, { casting: body })
}

export function castingUnarchive (id: number) {
  return httpPatch(`/${httpGetRoute()}/castings/${id}/unarchive`, {})
}

export function castingCustomerCancel (id: number, body: { archive_reason: CastingArchiveReason, other_archive_reason: string }): Promise<{ redirect_url: string | null }> {
  return httpPatch(`/${httpGetRoute()}/castings/${id}/refuse`, { casting: body })
}

export function castingSendExpired (id: number, body: { email_body: string, email_subject: string, email_close: boolean }) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/send_expired`, { casting: body })
}

export function castingSendNoEmployeeAvailable (id: number, body: { email_body: string, email_subject: string, email_close: boolean }) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/send_no_employee_available`, { casting: body })
}

export function castingSendSupplierRefuseToMarketplace (id: number, body: { email_body: string, email_subject: string }) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/send_supplier_refuse_to_marketplace`, { casting: body })
}

export function castingSendNoNewQuotation (id: number, body: { email_body: string, email_subject: string }) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/send_no_new_quotation`, { casting: body })
}

export function castingCountPerScope (): Promise<BackofficeCastingsCountPerScopeResponse> {
  return httpGet(`/${httpGetRoute()}/castings/count_per_scope`)
}

export function castingEditPricesAndDiscount (id: number): Promise<BackofficeCastingEditPriceAndDiscountResponse> {
  return httpGet(`/${httpGetRoute()}/castings/${id}/edit_prices_and_discount`)
}

export function castingUpdatePricesAndDiscount (id: number, body: { promotional_code_id?: number, override_rate_price_cents?: number, override_rate_tax_cents?: number }) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/update_prices_and_discount`, body)
}

export async function castingSendProposals (id: number, body: any, integrityCheck:{ ui_casting_state: CastingBase['state'], ui_proposal_ids: ProposalBase['id'][] }) {
  return httpRecoverable('POST', `/${httpGetRoute()}/castings/${id}/send_proposal`, { casting: body, ...integrityCheck }, 'casting')
}

export async function castingSendApppointmentProposals (id: number) {
  return httpRecoverable('POST', `/${httpGetRoute()}/castings/${id}/send_appointment_proposal`, {}, 'casting')
}

export function castingSetEmployeeProposals (id: number, body: { employee_proposals: { id?: number, slot_index: number, duration: number, suggested_start_dates: string[] }[] }) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/set_employee_proposals`, body)
}

export function castingSetAppointmentProposal (id: number, body: { appointment_proposal: any }): Promise<EmployeeCastingSetAppointmentProposalResponse> {
  return httpPost(`/${httpGetRoute()}/castings/${id}/set_appointment_proposal`, body)
}

export function castingAssignPendingLodgingIcalEvents (id: number) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/assign_pending_lodging_ical_events`, {})
}

export function castingReject (id: number) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/reject`, {})
}

export function castingRemoveScheduleDraftFlag (id: number) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/remove_schedule_draft_flag`, {})
}

export function castingPostulateCountinousAssignment (id: number, body: CastingPostulateCountinousAssignmentBody) {
  return httpPost(`/${httpGetRoute()}/castings/${id}/postulate_countinous_assignment`, body)
}

export function castingWhyThisEmployeeIsMissing (id: number, employeeId: EmployeeBase['id']): Promise<WhyThisEmployeeIsMissingResponse> {
  return httpPost(`/${httpGetRoute()}/castings/${id}/why_this_employee_is_missing`, { why_this_employee_is_missing_params: { employee_id: employeeId } })
}

export function castingBroadcastSessionsHistory (id: number, scheduleId?: ScheduleBase['id']): Promise<BackofficeCastingBroadastSessionsHistoryResponse> {
  return httpGet(`/${httpGetRoute()}/castings/${id}/broadcast_sessions_history`, { cancelled_schedule_id: scheduleId })
}

export function castingBroadcastEmployeesRankingWithMetrics (id: number): Promise<BackofficeCastingBroadcastEmployeesRankingWithMetricsResponse> {
  return httpGet(`/${httpGetRoute()}/castings/${id}/broadcast_employees_ranking_with_metrics`)
}

export function castingBroadcastToEmployees (id: number): Promise<BackofficeCastingBroadcastToEmployeesResponse> {
  return httpPost(`/${httpGetRoute()}/castings/${id}/broadcast_to_employees`, {})
}

export function castingValidateBoughtCouponsPack (id: number): Promise<{ invoice_id: number }> {
  return httpPost(`/${httpGetRoute()}/castings/${id}/validate_bought_coupons_pack`, {})
}

export function castingProposalPriceBlockData (params: { proposal_id: ProposalBase['id'], casting_id?: CastingBase['id'] }): Promise<CustomerServiceDeliveryPriceBlockDataResponse> {
  return httpGet(`/${httpGetRoute()}/castings/${params.casting_id}/price_block_data`, params)
}

export function castingSlotAllowedPatternFirstDate (dayId: CastingDayId, fromDate: string | Date) {
  if (!fromDate) fromDate = undefined // moment fallbacks to today when undefined, but not when null
  const date = moment(fromDate).day(dayId)
  if (date.valueOf() < moment(fromDate).valueOf()) date.add(1, 'week')
  return date
}

export function castingUnserializeSlot (slot: CastingSlot): UnserializedSlot {
  if (!slot.start_date) throw new Error('Cannot unserialize slot without start_date, infinite loop would occur.')

  return {
    duration: slot.duration,
    weeks_interval: slot.weeks_interval,
    start_date: moment.parseZone(slot.start_date),
    end_date: slot.end_date ? moment.parseZone(slot.end_date) : null,
    employees_count: slot.employees_count,
    allowed_patterns: slot.allowed_patterns.map(allowedPattern => {
      const realStartDate = moment.parseZone(slot.start_date).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      while (realStartDate.day() !== allowedPattern.day_id) realStartDate.add(1, 'day')

      const fromHour = realStartDate.clone().hours(parseInt(allowedPattern['from_hour'].split(':')[0], 10)).minutes(parseInt(allowedPattern['from_hour'].split(':')[1], 10))
      const toHour = realStartDate.clone().hours(parseInt(allowedPattern['until_hour'].split(':')[0], 10)).minutes(parseInt(allowedPattern['until_hour'].split(':')[1], 10))
      
      if (typeof allowedPattern.additional_days === 'number') {
        // If offered_service.rentable_unit_of_work === 'day', allowed pattern duration may exceed 24h.
        // Due to the way ['from_hour'] and ['until_hour'] are encoded (with modulo 24h),
        // the number of days is stored under ['additional_days'].
        toHour.add(allowedPattern.additional_days, 'days')
      } else if (toHour <= fromHour) {
        // toHour could be the same day or the next day (an event duration is 24h max)
        toHour.add(1, 'day')
      }

      return { day_id: allowedPattern.day_id, from_hour: fromHour, until_hour: toHour }
    })
  }
}

export function castingGenerateSlotOccurences (unserializedSlot: UnserializedSlot, until: moment.Moment) {
  if (unserializedSlot.end_date && unserializedSlot.end_date.valueOf() < until.valueOf()) until = unserializedSlot.end_date

  return unserializedSlot.allowed_patterns.map(allowedPattern => {
    const occurrences: { start: moment.Moment, end: moment.Moment }[] = []

    while (unserializedSlot.weeks_interval || occurrences.length === 0) {
      const newOccurrence = {
        start: allowedPattern.from_hour.clone().add(occurrences.length * (unserializedSlot.weeks_interval || 0), 'weeks'),
        end: allowedPattern.until_hour.clone().add(occurrences.length * (unserializedSlot.weeks_interval || 0), 'weeks')
      }

      if (newOccurrence.start.valueOf() > until.valueOf()) break
      occurrences.push(newOccurrence)
    }

    return occurrences
  })
}

export function castingsSlotAllowingAnyTimeOn7Days (slot: CastingSlot) {
  return slot.allowed_patterns.length === 7 && !slot.allowed_patterns.find(pattern => pattern.from_hour !== '00:00' || pattern.until_hour !== '24:00')
}

export function castingsUnserializedSlotAllowingAnyTimeOn7Days (unserializedSlot: UnserializedSlot) {
  return unserializedSlot.allowed_patterns.length === 7 && !unserializedSlot.allowed_patterns.find(
    pattern =>
      pattern.from_hour.hour() !== 0 ||
      pattern.from_hour.minute() !== 0 ||
      pattern.until_hour.hour() !== 0 ||
      pattern.until_hour.minute() !== 0
  )
}

export function castingSlotToI18n (
  unserializedSlot: UnserializedSlot,
  offeredService: Pick<OfferedServiceBase, 'flexible_start_date_in_absence_of_start_time_and_duration'>,
  dayOffs: Pick<DayOffBase, 'date' | 'name'>[],
  reactFragment = false
) {
  const parts: (string | React.ReactElement)[] = []
  const alternative = unserializedSlot.allowed_patterns.length > 1 ? 'alternative_' : ''
  const formattedDuration = unserializedSlot.duration === null
    ? i18nScoped.t('duration_on_quotation')
    : unserializedSlot.employees_count > 1
    ? i18nScoped.t('duration_mul_employees', {
      duration: formatDurationFromSecs(unserializedSlot.duration / unserializedSlot.employees_count),
      employees_count: unserializedSlot.employees_count
    })
    : formatDurationFromSecs(unserializedSlot.duration)

  if (unserializedSlot.weeks_interval) {
    const headerRow = `${i18nScoped.t('reccurring')}, ${castingWeeksInterval(unserializedSlot.weeks_interval, false)} (${formattedDuration})`
    parts.push(reactFragment ? React.createElement('b', { className: 'u-txtClr1' }, headerRow) : headerRow)
  } else {
    const headerRow = `${i18nScoped.t('one_shot')} (${formattedDuration})`
    parts.push(reactFragment ? React.createElement('b', { className: 'u-txtClr1' }, headerRow) : headerRow)
  }

  const tWithDayOffsReactFragment = (path: string, firstDate: moment.Moment | null, i18nVars: any = {}) => {
    if (reactFragment) {
      const tooltipProps: Partial<TooltipComponentProps> = {
        noIcon: true,
        innerStyle: { width: 'fit-content', whiteSpace: 'nowrap', left: 0, right: 'unset', marginBottom: 0 },
        style: { display: 'inline-flex' }
      }
      
      const getDateWithDayOffTooltip = (dateField: string) => React.createElement(
        TooltipComponent,
        { ...tooltipProps, message: foundDayOff?.name },
        i18nVars[dateField],
        foundDayOff && React.createElement('span', { className: 'label label--small u-mlxs' }, i18nScoped.t('day_off'))
      )

      const foundDayOff = firstDate ? dayOffs.find(dayOff => moment(dayOff.date).isSame(firstDate, 'day')) : null
      return i18nScoped.tFragment(path, { ...i18nVars, ... Object.fromEntries(['first_date', 'date', 'from_date', 'start'].map(dateField => [dateField, getDateWithDayOffTooltip(dateField)])) })
    }

    return i18nScoped.t(path, i18nVars)
  }

  if (!castingsUnserializedSlotAllowingAnyTimeOn7Days(unserializedSlot)) {
    for (const allowedPattern of unserializedSlot.allowed_patterns.slice().sort((a, b) => a.until_hour.valueOf() - b.until_hour.valueOf())) {
      const multipleDays = allowedPattern.until_hour.diff(allowedPattern.from_hour, 'day', true) > 1
      const sParts: (string | React.ReactElement)[] = []

      if (unserializedSlot.weeks_interval) {
        const firstDate = allowedPattern.from_hour.format('L')
        if (multipleDays) sParts.push(tWithDayOffsReactFragment(`reccurring_${alternative}multiple_days`, allowedPattern.from_hour, { from_day_name: allowedPattern.from_hour.format('dddd'), until_day_name: allowedPattern.until_hour.clone().subtract(1, 'second').format('dddd'), first_date: firstDate }))
        else sParts.push(tWithDayOffsReactFragment(`reccurring_${alternative}pattern`, allowedPattern.from_hour, { day_name: allowedPattern.from_hour.format('ddd'), first_date: firstDate }))
      } else {
        if (multipleDays) sParts.push(tWithDayOffsReactFragment(`one_shot_${alternative}multiple_days`, allowedPattern.from_hour, { from_date: allowedPattern.from_hour.format('LL'), until_date: allowedPattern.until_hour.clone().subtract(1, 'second').format('LL') }))
        else sParts.push(tWithDayOffsReactFragment(`one_shot_${alternative}pattern`, allowedPattern.from_hour, { date: allowedPattern.from_hour.format('LL') }))
      }

      if (multipleDays) {
        // multi-days slots doest not show the time part, so this in a NO-OP.
      } else if (allowedPattern.until_hour.diff(allowedPattern.from_hour, 'day', true) === 1 && allowedPattern.from_hour.hour() === 0 && allowedPattern.from_hour.minute() === 0) {
        sParts.push(' ' + i18nScoped.t('the_whole_day'))
      } else if (!allowedPattern.from_hour.isSame(allowedPattern.until_hour, 'day')) {
        sParts.push(' ' + i18nScoped.t('between_hours_next_day', { from_hour: allowedPattern.from_hour.format('LT'), until_hour: allowedPattern.until_hour.format('LT') }))
      } else {
        sParts.push(' ' + i18nScoped.t('between_hours', { from_hour: allowedPattern.from_hour.format('LT'), until_hour: allowedPattern.until_hour.format('LT') }))
      }

      parts.push(
        reactFragment
          ? React.createElement(React.Fragment, null, ...sParts.map((sPart, i) => React.createElement(React.Fragment, { key: i }, sPart)))
          : sParts.join('')
        )
    }
  } else if (offeredService.flexible_start_date_in_absence_of_start_time_and_duration) {
    parts.push(tWithDayOffsReactFragment('prefered_flexible_start_date', unserializedSlot.start_date, { date: unserializedSlot.start_date.format('LL') }))
  } else {
    parts.push(tWithDayOffsReactFragment('any_time_on_7_days', unserializedSlot.start_date, { start: unserializedSlot.start_date.format('LL'), end: unserializedSlot.start_date.clone().add(6, 'days').format('LL') }))
  }

  if (unserializedSlot.weeks_interval) parts.push(unserializedSlot.end_date ? i18nScoped.t( 'end_date', { date:unserializedSlot.end_date.format('L') }) : i18nScoped.t('end_date_nil'))

  return reactFragment ? parts.map((row, i) => React.createElement('div', { key: i }, row)) : parts.join("\n")
}

export function castingI18nArchiveReason (reason: CastingArchiveReason) {
  return i18nScoped.t(`archive_reason${window.currentUser.type === 'Customer' ? '_customer' : ''}.${reason}`)
}

export function castingI18nCastingType (castingType: CastingType, prefix = 'casting_type'): string {
  return i18nScoped.t(`${prefix}.${castingType}`)
}

export function castingI18nScope (scope: keyof BackofficeCastingsCountPerScopeResponse['stats'] | 'all') {
  return i18nScoped.t(`scope.${scope}`)
}

export function castingI18nRequiredPlace (casting: Pick<CastingBase, 'required_place' | 'city' | 'zipcode'> & Record<'service_delivery_location' | 'market', { address: { inline: string } }>) {
  switch (casting.required_place) {
    case 'on_site':
      return (casting.service_delivery_location || casting.market).address.inline
    case 'online':
    case 'phone':
      return offeredServiceI18nInterventionPlace(casting.required_place)
    default:
      return casting.zipcode ? `${casting.city} (${casting.zipcode})` : casting.city
  } 
}

export function castingI18nFrequency (casting: Pick<CastingBase, 'casting_type' | 'slots'>) {
  if (casting.casting_type === 'casting_recurring' && casting.slots.every(slot => slot['weeks_interval'] == casting.slots[0]['weeks_interval'])) {
    return i18nScoped.t('answer.frequencies', { count: casting.slots[0]['weeks_interval'] })
  }
  return castingI18nCastingType(casting.casting_type, 'casting_model_type')
}

export function castingWeeksInterval (weeksInterval: number, capitalize = true): string {
  const str = i18nScoped.t(`all_x_weeks.${weeksInterval}`)
  return capitalize ? toUpperCaseFirst(str) : str
}

export function castingPostSubmitRedirectUrl (castingMode: CastingMode, casting: { id: number, state: CastingState }, editedCastingId: CastingBase['id'], proposalId?: ProposalBase['id']) {
  const statesAllowingAnswer: CastingState[] = ['created', 'appointment_resolved', 'supplier_no_employee_available', 'waiting_employee_assignment', 'customer_refused']
  
  if (window.currentUser?.type === 'Operator' && window.currentUser.market_type === 'Supplier' && statesAllowingAnswer.includes(casting.state)) {
    return `/${httpGetRoute()}/castings/${casting.id}/answer`
  } else if (editedCastingId) {
    return `/${(window.currentUser.market_type === 'Marketplace' ? 'market' : 'backoffice')}/castings/${casting.id}/answer`
  } else if (castingMode === 'indirect' || window.currentUser?.type === 'Operator' || window.currentUser?.type === 'Employee' || !proposalId) {
    const { market_type, market_id } = window.currentUser || window
    return `/${(market_type === 'Marketplace' ? 'marketplaces' : 'suppliers')}/${market_id}/castings/sent`
  } else if (casting.state === 'supplier_resolved') {
    return `/account/castings/${casting.id}`
  } else {
    return `/account/proposals/${proposalId}/service_deliveries/create`
  }
}

/**
 * Front-end implementation of the Casting#flatten_multi_day_slots method (should pass the same suite of tests).
 *
 * Some slots may have a 'additional_day' field in the allowed_pattern.
 * In such cases, that slot lasts multiple days.
 * When generating events for multi-days events, we generate exactly one event per day.
 * This method generates a list of slots (one slot per day) from the original slot.
 * Each from_hour and until_hour is aligned on the offered_service's multi_opening_hours.
 * Those slots are then used for searching new availabilities or when creating schedules.
 */
export function castingFlattenMultiDaySlots (slot: CastingSlot, offeredService: { multi_opening_hours: MultiOpeningHours }, startTime?: moment.Moment) {
  if (slot.allowed_patterns.length !== 1) throw new Error('Does not support multiple allowed_patterns')

  const flattenSlots: CastingSlot[] = []
  startTime ||= castingUnserializeSlot(slot).allowed_patterns[0].from_hour

  for (let dayOffset = 0; dayOffset < slot.allowed_patterns[0].additional_days; dayOffset++) {
    const day = startTime.clone().add(dayOffset, 'days')
    const moh = offeredService.multi_opening_hours ? offeredService.multi_opening_hours[openableDateToWorkingDay(day.toDate())] : [['00:00', '24:00']]
    const fromHour = moh ? moh[0][0] : '00:00'
    const untilHour = moh ? moh[moh.length - 1][1] : '24:00'

    flattenSlots.push({
      duration: Math.round((parseTime(untilHour) - parseTime(fromHour)) * 3600),
      weeks_interval: slot.weeks_interval,
      start_date: day.format('YYYY-MM-DD'),
      end_date: slot.end_date,
      allowed_patterns: [{
        day_id: day.day(),
        from_hour: fromHour,
        until_hour: untilHour,
        additional_days: null
      }]
    })
  }

  return flattenSlots
}

export function castingAvailabilityHasSuggestedDate ({ allowed_patterns }: Availability) {
  return !!allowed_patterns.find(allowedPattern => allowedPattern.suggested_dates.length)
}

/** returns the first (if it exists) availabity that is inside the allowed_pattern and fullfill the required duration */
export function castingFindInsidePatternAvailability ({ allowed_patterns }: Availability, minDuration: number) {
  for (const patternAvailability of allowed_patterns) {
    const found = patternAvailability.inside_pattern.find(_ => _.duration >= minDuration)
    if (found) return found
  }

  return null
}

export function castingExtendsAllowedPatternOnDurationChanged (slots: CastingSlot[], additionalDuration: number) {
  slots = JSON.parse(JSON.stringify(slots))

  for (const slot of slots) {
    for (const allowedPattern of slot.allowed_patterns) {
      const parsedRange = parseTimeRange(allowedPattern.from_hour, allowedPattern.until_hour)
      const patternDuration = (parsedRange[1] - parsedRange[0]) * 3600

      // extends the pattern range if possible (and seems logical from user perspective)
      if (slot.duration === patternDuration && patternDuration + additionalDuration < 24 * 3600) {
        allowedPattern.until_hour = formatTime((parsedRange[1] + (additionalDuration / 3600)) % 24) 
      }
    }

    slot.duration += additionalDuration
  }

  return slots
}

export function castingTimeUnitIsDay (slots: CastingSlot[]) {
  return !!slots.find(slot => slot['allowed_patterns'].find (allowedPattern => allowedPattern.additional_days > 0))
}

/**
 * Front-end implementation of the Casting#simulate_first_events_from_unfinalized_casting method (should pass the same suite of tests).
 *
 * To compute the price of the casting, we must work on each slot, its start-date, its end-date, etc.
 * Duration and order of each event is signifiant (because specific price rules depend on them, because the order matter for promotional codes, etc.).
 * But when this method is called, the schedules and events are not yet created. So we have to simulate them.
 * This implementation only works with the casting.slots (and not the proposal).
 */
 export function castingSimulateFirstEventsFromUnfinalizedCasting ({ casting, offeredService, limit, onlyFirstRecurrence }: SimulateFirstEventsFromUnfinalizedCastingArguments) {
  if (!limit && !onlyFirstRecurrence) throw new Error('Please set :limit or :onlyFirstRecurrence')

  const rules = (castingTimeUnitIsDay(casting.slots) ? castingFlattenMultiDaySlots(casting.slots[0], offeredService) : casting.slots)
    .map(slot => castingUnserializeSlot(slot))
    .map(unserializedSlot => {
      const realMinStartTime = moment.min(unserializedSlot.allowed_patterns.map(ap => ap.from_hour))
      const weeksInterval = onlyFirstRecurrence ? null : unserializedSlot.weeks_interval
      return { startDate: realMinStartTime, weeksInterval, endDate: unserializedSlot.end_date?.endOf('day') || null, duration: unserializedSlot.duration }
    })

  const events = rules.flatMap(rule => castingOccurencesFromBasicScheduleRules(rule, limit))
  events.sort((a, b) => a.startDate.valueOf() - b.startDate.valueOf())
  return limit ? events.slice(0, limit) : events
}

function castingOccurencesFromBasicScheduleRules (rule: BasicScheduleRule, limit?: number) {
  const events: { startDate: moment.Moment, duration: number }[] = []
  const startDate = rule.startDate.clone()

  do {
    events.push({ startDate: startDate.clone(), duration: rule.duration })
    startDate.add(rule.weeksInterval, 'week')
  } while (rule.weeksInterval && (!rule.endDate || startDate < rule.endDate) && (!limit || events.length < limit))

  return events
}
